// ============================================================================
// reloadHold — segura a recarga automática do app durante uma batida de ponto
// ============================================================================
// O VersionChecker (deploy novo) e o PWAUpdateNotice (service worker novo)
// recarregam a página sozinhos. Se isso acontece no meio de uma batida (GPS,
// envio, conferência), a resposta se perde e a pessoa não sabe se bateu.
//
// Quem está no meio de algo importante chama holdReload(); os recarregadores
// esperam whenReloadAllowed() antes de recarregar. Duas garantias contra
// travar a atualização para sempre:
//   · cada trava vence sozinha em no máximo 45 s (mesmo sem release);
//   · whenReloadAllowed() desiste de esperar em no máximo 60 s.
// ============================================================================

export const MAX_HOLD_MS = 45_000;
export const MAX_WAIT_MS = 60_000;

const travas = new Map(); // id -> vence em (ms)
let seq = 0;

function limpar(agora = Date.now()) {
  for (const [id, vence] of travas) if (vence <= agora) travas.delete(id);
}

// Segura a recarga. Devolve a função que solta a trava (pode chamar mais de uma vez).
export function holdReload(ms = MAX_HOLD_MS) {
  const dur = Math.max(0, Math.min(Number(ms) || MAX_HOLD_MS, MAX_HOLD_MS));
  const id = ++seq;
  travas.set(id, Date.now() + dur);
  return () => { travas.delete(id); };
}

export function isReloadHeld() {
  limpar();
  return travas.size > 0;
}

// Resolve quando não houver trava ativa, ou quando estourar a espera máxima.
export function whenReloadAllowed({ maxWaitMs = MAX_WAIT_MS, pollMs = 500 } = {}) {
  const limite = Date.now() + Math.min(Math.max(0, Number(maxWaitMs) || 0), MAX_WAIT_MS);
  return new Promise((resolve) => {
    const tick = () => {
      if (!isReloadHeld() || Date.now() >= limite) { resolve(); return; }
      setTimeout(tick, pollMs);
    };
    tick();
  });
}

// ============================================================================
// Trava SEM teto (CRM_SEM_RECARGA, 27/10/2026): o CRM da recepção com texto no
// campo de resposta, com o campo em foco ou com um envio saindo. Diferente da
// trava do ponto (45 s / 60 s, que continua igual e separada): enquanto ela
// existe, os vigias NÃO recarregam; guardam "versão nova pendente" e a tela
// mostra o aviso "Versão nova · Atualizar". Recarrega quando a trava soltar
// (e ficar solta por ESTAVEL_MS: o blur antes do clique no Enviar não conta)
// ou no clique do aviso. Quem decide é decidirRecarga (pura, testada).
// ============================================================================

export const ESTAVEL_MS = 2_000;

const semTeto = new Map(); // id -> motivo
const ouvintes = new Set();
let pendente = null;          // { origem, em } | null
let recarregador = null;      // a função do VersionChecker (limpa caches e recarrega)
let emCurso = false;          // um vigia só recarrega, uma vez

function avisar() {
  const estado = estadoRecarga();
  for (const fn of [...ouvintes]) { try { fn(estado); } catch { /* ouvinte quebrado não derruba */ } }
}

// Segura a recarga sem teto. Devolve a função que solta (pode chamar mais de uma vez).
export function holdReloadSemTeto(motivo = 'crm') {
  const id = ++seq;
  semTeto.set(id, String(motivo || 'crm'));
  avisar();
  return () => { if (semTeto.delete(id)) avisar(); };
}

export function isReloadHeldSemTeto() { return semTeto.size > 0; }

/**
 * A decisão de um vigia (VersionChecker ou service worker) diante de uma versão nova.
 *   'esperar'    : a trava do ponto existe (o vigia espera whenReloadAllowed, como sempre);
 *   'adiar'      : o interruptor ligado e a trava sem teto (CRM): guarda pendente, não recarrega;
 *   'nada'       : outro vigia já está recarregando;
 *   'recarregar' : pode recarregar agora.
 */
export function decidirRecarga({ ligado = false, travaPonto = isReloadHeld(), travaSemTeto = isReloadHeldSemTeto(), recarregando = emCurso } = {}) {
  if (recarregando) return 'nada';
  if (ligado && travaSemTeto) return 'adiar';
  if (travaPonto) return 'esperar';
  return 'recarregar';
}

export function marcarVersaoPendente(origem = 'versao') {
  if (!pendente) pendente = { origem, em: Date.now() };
  avisar();
  try { if (typeof window !== 'undefined') window.dispatchEvent(new Event('fk-versao-nova')); } catch { /* sem window */ }
}

export function estadoRecarga() {
  return { pendente: !!pendente, travadaSemTeto: semTeto.size > 0, recarregando: emCurso };
}

export function ouvirRecarga(fn) {
  ouvintes.add(fn);
  return () => { ouvintes.delete(fn); };
}

// Um vigia só, uma vez: devolve true para quem ganhou o direito de recarregar.
export function iniciarRecarga() {
  if (emCurso) return false;
  emCurso = true;
  try { if (typeof window !== 'undefined') window.__fkRecarregando = true; } catch { /* sem window */ }
  avisar();
  return true;
}
export function recargaEmCurso() { return emCurso; }

export function registrarRecarregador(fn) {
  recarregador = typeof fn === 'function' ? fn : null;
  return () => { if (recarregador === fn) recarregador = null; };
}

// O clique em "Atualizar" (e a trava que soltou): recarrega uma vez só.
export async function recarregarAgora() {
  if (!iniciarRecarga()) return false;
  try {
    if (recarregador) await recarregador();
    else if (typeof window !== 'undefined') window.location.reload();
  } catch {
    try { window.location.reload(); } catch { /* sem window */ }
  }
  return true;
}

// Resolve quando a trava sem teto estiver solta há `estavelMs` seguidos (sem teto de espera).
export function quandoSoltarSemTeto({ estavelMs = ESTAVEL_MS, pollMs = 500 } = {}) {
  return new Promise((resolve) => {
    let soltaDesde = null;
    const tick = () => {
      if (isReloadHeldSemTeto()) soltaDesde = null;
      else if (soltaDesde == null) soltaDesde = Date.now();
      if (soltaDesde != null && Date.now() - soltaDesde >= estavelMs) { resolve(); return; }
      setTimeout(tick, pollMs);
    };
    tick();
  });
}

// Só para testes.
export function _resetReloadHolds() {
  travas.clear(); semTeto.clear(); pendente = null; recarregador = null; emCurso = false;
  try { if (typeof window !== 'undefined') delete window.__fkRecarregando; } catch { /* sem window */ }
}
